import React, { useState, useEffect } from "react";
import { Avatar, Skeleton, SkeletonItem, Tooltip } from "@fluentui/react-components";
import {
  BriefcaseIcon,
  CurrencyIcon,
  FolderIcon,
  PersonIcon,
  TimerIcon,
  ProjectIcon,
} from "../../../modules/default-svg";
import HoverCard from "../../../components/hoverCard/HoverCard";
import {
  getAccountCurrency,
  displayDecimalFormatted,
  weeksBetweenDates
} from "../../../utils/systemSettings";
import { useHistory } from "react-router-dom";
import useNavigation from "../../../utils/useNavigation";
import ResourceDetails from "../../resourcePlanner/resourceDetails/ResourceDetails";
import Modal from "../../../components/controls/modal/Modal";
import { apiInstance, routes } from "../../../helpers/API";
import { useDispatch } from "react-redux";
import { generalActions } from "../../../store/actions";
import SafeHtml from "../../../components/safeHtml/SafeHtml";
import { CalendarClock, LayoutGrid, Network } from "lucide-react";
import projectService from "../../../store/services/projects.service";
import TeamManagement from "../../../components/teamManagement/TeamManagement";
import ContactsPanel from "../../../components/contactsPanel/ContactsPanel";
import ExportButton from "../../../components/Export/ExportButton";
import HierarchyChain from "../../../components/entityDetails/HierarchyChain";
import DetailStat from "../../../components/entityDetails/DetailStat";
import DetailContactCard from "../../../components/entityDetails/DetailContactCard";
import entityDetailsStyles from "../../../components/entityDetails/EntityDetails.module.scss";
import { getLabelByType } from "../../../helpers/utils";

const ProjectDetails = (props) => {
  const { projectData, isHeaderCollapsed } = props;
  const { navigateTo } = useNavigation();
  const displatch = useDispatch();
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [isResourceDetailsLoading, setIsResourceDetailsLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(false);
  const [resourceTasks, setResourceTasks] = useState([]);
  const [setting] = useState(settings);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [resources, setResources] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("byScope");
  const [leftTab, setLeftTab] = useState("details");
  const [contactView, setContactView] = useState("cards");
  const leftPanelMaxHeight = `calc(100vh - ${isHeaderCollapsed ? 300 : 450}px)`;

  const handleRedirection = (type, id, name) => {
    switch (type) {
      case "Client": {
        // Use navigation hook for direct navigation to client
        navigateTo("Clients", id, projectData.client.name);
        break;
      }
      case "Portfolio": {
        // Use navigation hook for direct navigation to portfolio
        navigateTo("Portfolios", id, projectData.portfolio.name);
        break;
      }
      case "Program": {
        // Use navigation hook for direct navigation to program
        navigateTo("Programs", id, projectData.program.name);
        break;
      }
      case "Scope": {
        navigateTo("ScopeItems", id, name);
        break;
      }
      default:
        return "";
    }
  };

  const buildChainNodes = () => {
    const nodes = [];
    if (projectData.client) {
      nodes.push({
        key: "client",
        type: "Client",
        icon: <PersonIcon />,
        name: projectData.client.name,
        onClick: () => handleRedirection("Client", projectData.client.id),
      });
    }
    if (projectData.portfolio) {
      nodes.push({
        key: "portfolio",
        type: "Portfolio",
        icon: <BriefcaseIcon />,
        name: projectData.portfolio.name,
        onClick: () => handleRedirection("Portfolio", projectData.portfolio.id),
      });
    }
    if (projectData.program) {
      nodes.push({
        key: "program",
        type: "Program",
        icon: <FolderIcon />,
        name: projectData.program.name,
        onClick: () => handleRedirection("Program", projectData.program.id),
      });
    }
    nodes.push({ key: "current", type: "Project", icon: <ProjectIcon />, name: projectData.name, isCurrent: true });
    return nodes;
  };

  const getResourceContribution = async (id) => {
    try {
      setIsResourceDetailsLoading(true);
      setResourceTasks([]);
      const response = await apiInstance.get(
        `${routes.getContribution.replace(":resourceId", id)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage
              ?.getItem("accessToken")
              ?.replace(/"/g, "")}`,
          },
          params: {
            ProjectId: projectData.id,
          },
        }
      );
      const {
        data: { resource, allocatedCapacity, totalCapacity, tasks },
      } = response;
      setIsResourceDetailsLoading(false);
      setUserDetails({ ...resource, allocatedCapacity, totalCapacity });
      setResourceTasks(tasks.length > 0 ? tasks : []);
    } catch (error) {
      setIsResourceDetailsLoading(false);
      return displatch(
        generalActions.showToast({
          title: error.message,
          type: "error",
        })
      );
    }
  };

  const fetchResources = () => {
    setIsLoading(true);
    projectService.getTeam(projectData.id)
      .then((response) => {
        setResources(response);
        setIsLoading(false);
      }
      )
      .catch((error) => { })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (projectData?.id) {
      fetchResources();
    }
  }, [projectData]);

  const handleTeamUpdate = () => {
    fetchResources();
  };

  const handleCancel = () => setShowDetailModal(false);
  const handleOpen = (user) => {
    setUserDetails(user);
    setShowDetailModal(true);
    getResourceContribution(user.id);
  };

  return (
    <div className="DetailsTabScroll">
      <div className="row">
        {projectData && (
          <>
            <div className="col-lg-8">
              <div className="CustomCard p-4">
                <div className={entityDetailsStyles.panelHeaderRow}>
                  <div className="resourcesTabToggleContainer">
                    <button
                      className={`resourcesTabToggleButton ${leftTab === "details" ? "active" : ""}`}
                      onClick={() => setLeftTab("details")}
                    >
                      Details
                    </button>
                    <button
                      className={`resourcesTabToggleButton ${leftTab === "contact" ? "active" : ""}`}
                      onClick={() => setLeftTab("contact")}
                    >
                      Contacts
                    </button>
                  </div>

                  {leftTab === "contact" && (
                    <div className={entityDetailsStyles.viewToggle}>
                      <button
                        className={`${entityDetailsStyles.viewToggleButton} ${contactView === "cards" ? entityDetailsStyles.active : ""}`}
                        onClick={() => setContactView("cards")}
                      >
                        <LayoutGrid size={13} /> Cards
                      </button>
                      <span className={entityDetailsStyles.viewToggleDivider} />
                      <button
                        className={`${entityDetailsStyles.viewToggleButton} ${contactView === "orgChart" ? entityDetailsStyles.active : ""}`}
                        onClick={() => setContactView("orgChart")}
                      >
                        <Network size={13} /> Org Chart
                      </button>
                    </div>
                  )}
                </div>

                <div className="detailsLeftPanelScroll" style={{ maxHeight: leftPanelMaxHeight }}>
                {leftTab !== "details" ? (
                  <ContactsPanel
                    embedded
                    parentType="Projects"
                    parentId={projectData.id}
                    parentName={projectData.name}
                    view={contactView}
                    onViewChange={setContactView}
                  />
                ) : (
                <>
                <div className={entityDetailsStyles.entityHeaderRow}>
                  {projectData.refId && (
                    <Tooltip content="Reference ID" relationship="label">
                      <p className="detailBadge mb-0">{projectData.refId}</p>
                    </Tooltip>
                  )}
                  <Tooltip content="Project Type" relationship="label">
                    <p className={`detailBadge mb-0 project-type-${projectData.type}`}>{getLabelByType("projectType", projectData.type)}</p>
                  </Tooltip>
                  <h5>{projectData.name}</h5>
                </div>

                <HierarchyChain nodes={buildChainNodes()} />

                <div className={entityDetailsStyles.statGrid}>
                  <div className={entityDetailsStyles.contactStatBlock}>
                    <p className={entityDetailsStyles.contactStatLabel}>Sponsor</p>
                    {projectData.sponsor ? (
                      <div className={entityDetailsStyles.miniContactGrid}>
                        <DetailContactCard contact={projectData.sponsor} />
                      </div>
                    ) : (
                      <p className={entityDetailsStyles.miniContactEmpty}>No sponsor assigned</p>
                    )}
                  </div>
                  <div className={entityDetailsStyles.contactStatBlock}>
                    <p className={entityDetailsStyles.contactStatLabel}>Executive</p>
                    {projectData.executive ? (
                      <div className={entityDetailsStyles.miniContactGrid}>
                        <DetailContactCard contact={projectData.executive} />
                      </div>
                    ) : (
                      <p className={entityDetailsStyles.miniContactEmpty}>No executive assigned</p>
                    )}
                  </div>
                  <DetailStat
                    icon={<CalendarClock size={16} strokeWidth={1.5} />}
                    label="Duration"
                    value={
                      projectData.startDate && (projectData.revisedEndDate || projectData.endDate)
                        ? weeksBetweenDates(projectData.startDate, projectData.revisedEndDate || projectData.endDate)
                        : null
                    }
                  />
                  <DetailStat
                    icon={<CurrencyIcon color="#342C82" />}
                    label="Budget"
                    value={
                      projectData.budget
                        ? `${getAccountCurrency(setting)}${displayDecimalFormatted(setting, projectData.budget)}`
                        : null
                    }
                  />
                  <div className={`${entityDetailsStyles.contactStatBlock} ${entityDetailsStyles.statTileWide}`}>
                    <p className={entityDetailsStyles.contactStatLabel}>Stakeholders</p>
                    {projectData.stakeholders && projectData.stakeholders.length > 0 ? (
                      <div className={entityDetailsStyles.miniContactGrid}>
                        {projectData.stakeholders.map((contact) => (
                          <DetailContactCard key={contact.id} contact={contact} />
                        ))}
                      </div>
                    ) : (
                      <p className={entityDetailsStyles.miniContactEmpty}>No stakeholders added</p>
                    )}
                  </div>
                </div>

                {projectData.description &&
                  <div className={entityDetailsStyles.descriptionCard}>
                    <h6 className={entityDetailsStyles.descriptionTitle}>Description</h6>
                    <p className={entityDetailsStyles.descriptionText}>
                      <SafeHtml html={projectData.description} />
                    </p>
                  </div>
                }
                </>
                )}
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="CustomCard p-4">
                <div className="d-flex justify-content-between align-items-center mb-20">
                  <div className="d-flex align-items-center gap-2">
                    <h4 className="mb-0">Team</h4>
                    
                    {/* Tabs */}
                    <div className="resourcesTabToggleContainer">
                      <button
                        className={`resourcesTabToggleButton ${activeTab === "byScope" ? "active" : ""}`}
                        onClick={() => setActiveTab("byScope")}
                      >
                        By Scope
                      </button>
                      <button
                        className={`resourcesTabToggleButton ${activeTab === "all" ? "active" : ""}`}
                        onClick={() => setActiveTab("all")}
                      >
                        All
                      </button>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <ExportButton config={{ category: 6, projectId: projectData?.id }} />
                    {isLoading ? (
                      <Skeleton>
                        <SkeletonItem style={{ width: "100px", height: "30px", borderRadius: "20px" }} />
                      </Skeleton>
                    ) : (
                      <TeamManagement
                        entityType={"project"}
                        entityId={projectData?.id}
                        parentId={projectData?.client?.id}
                        onTeamUpdate={handleTeamUpdate}
                        initialTeamUsers={resources ? resources.resources : []}
                      />
                    )}
                  </div>
                </div>

                <div className="detailsLeftPanelScroll" style={{ maxHeight: leftPanelMaxHeight }}>
                {isLoading ? (
                  [...Array(5)].map((_, index) => (
                    <div key={index} className="d-flex align-items-center CustomCardWrap pl-2 mb-3">
                      <Skeleton>
                        <SkeletonItem size={32} shape="circle" />
                      </Skeleton>
                      <div className="ms-12 flex-grow-1">
                        <Skeleton>
                          <SkeletonItem style={{ width: '60%', height: '20px' }} />
                        </Skeleton>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    {activeTab === "byScope" ? (
                      // By Scope View
                      resources && resources.resourcesByScope ? (
                        [...resources.resourcesByScope].sort((a, b) => (a.scope.name || '').localeCompare(b.scope.name || '')).map((item) => (
                          <div key={item.scope.id} className="bg-[#f9f8ff] rounded-lg p-3 mb-3">
                            <p
                              className="text-[13px] text-[#342c82] font-figtree font-semibold mb-2 hover:font-bold cursor-pointer"
                              onClick={() => handleRedirection("Scope", item.scope.id, item.scope.name)}
                            >
                              {item.scope.name}
                            </p>
                            {[...item.users].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((user) => (
                              <div
                                className="d-flex align-items-center CustomCardWrap pl-2"
                                key={user.id}
                              >
                                <HoverCard
                                  HoverCardTrigger={
                                    <Avatar
                                      color="colorful"
                                      size={28}
                                      name={user.name}
                                      image={{ src: user.image ? user.image : "" }}
                                    />
                                  }
                                  AvatarId={user.id}
                                  AvatarName={user.name}
                                  AvatarImage={user.image}
                                  AvatarDesignation={user.title}
                                  AvatarEmail={user.email}
                                  AvatarPhone={user.phoneNumber}
                                  AvatarIsExternal={user.isExternal}
                                />
                                {/* <div className="ms-12 hover:font-bold hover:underline cursor-pointer" onClick={() => handleOpen(user)}>
                                  <p className="mb-1 textColor3">{user.name}</p>
                                </div> */}
                                <div className="ms-12 resourceNameContainer">
                                  <span className="resourceName">{user.name}</span>
                                  {user?.title && <span className="resourceTitle">{user.title}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <p className="text-sm">No resources assigned by scope</p>
                        </div>
                      )
                    ) : (
                      // All View
                      resources && resources.resources ? (
                        [...resources.resources].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((user) => (
                          <div
                            className="d-flex align-items-center CustomCardWrap pl-2"
                            key={user.id}
                          >
                            <HoverCard
                              HoverCardTrigger={
                                <Avatar
                                  color="colorful"
                                  size={28}
                                  name={user.name}
                                  image={{ src: user.image ? user.image : "" }}
                                />
                              }
                              AvatarId={user.id}
                              AvatarName={user.name}
                              AvatarImage={user.image}
                              AvatarDesignation={user.title}
                              AvatarEmail={user.email}
                              AvatarPhone={user.phoneNumber}
                              AvatarIsExternal={user.isExternal}
                            />
                            {/* <div className="ms-12 hover:font-bold hover:underline cursor-pointer" onClick={() => handleOpen(user)}>
                              <p className="mb-1 textColor3">{user.name}</p>
                            </div> */}
                            <div className="ms-12 resourceNameContainer">
                                  <span className="resourceName">{user.name}</span>
                                  {user?.title && <span className="resourceTitle">{user.title}</span>}
                                </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <p className="text-sm">No team members yet</p>
                        </div>
                      )
                    )}
                  </>
                )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showDetailModal && (
        <Modal
          title="Resource Details"
          footer={false}
          handleCancel={() => handleCancel(false)}
          handleSave={() => handleCancel(false)}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          isLoading={false}
          size="large"
          calendarModalWrap={true}
          body={
            <ResourceDetails
              isLoading={isResourceDetailsLoading}
              userDetails={userDetails}
              shift={null}
              tasks={resourceTasks}
              isProjects={false}
              isTasks={true}
              defaultTab="tasks"
            />
          }
        />
      )}
    </div>
  );
};
export default ProjectDetails;
